import { z } from 'zod'

/** Zod schemas  */

export const actionTypes = [
  'contactForm',
  'email',
  'facebook',
  'instagram',
  'tiktok',
  'linkedin',
  'twitter',
  'petition',
  'call',
  'youtube',
  'other',
] as const

export type ActionType = (typeof actionTypes)[number]

export const zPreferences = z.object({
  contactForm: z.boolean().optional().default(true),
  email: z.boolean().optional().default(true),
  facebook: z.boolean().optional().default(true),
  instagram: z.boolean().optional().default(true),
  tiktok: z.boolean().optional().default(true),
  linkedin: z.boolean().optional().default(true),
  twitter: z.boolean().optional().default(true),
  petition: z.boolean().optional().default(true),
  call: z.boolean().optional().default(true),
  youtube: z.boolean().optional().default(true),
  other: z.boolean().optional().default(true),
})

export const zHints = zPreferences.extend({
  'petition-contentful-petition': z.boolean().optional().default(true),
  'petition-contentful-protest': z.boolean().optional().default(true),
})

export type Preferences = z.input<typeof zPreferences>
export type Hints = z.input<typeof zHints>

export const zActions = z.object({
  done: z
    .array(
      z.object({
        id: z.string(),
        doneDate: z.string(),
        title: z.string().optional().default(''),
        type: z
          .enum(['', ...actionTypes])
          .optional()
          .default(''),
      }),
    )
    .default([]),
  score: z.number().optional().default(0),
  hidden: z.array(z.string()).optional().default([]),
  snoozed: z
    .array(
      z.object({
        id: z.string().optional().default(''),
        timeStamp: z.number().optional().default(0),
      }),
    )
    .optional()
    .default([]),
  weekScore: z
    .object({
      timeStamp: z.number().optional().default(0),
      score: z.number().optional().default(0),
      longest: z.number().optional().default(0),
    })
    .default({ timeStamp: 0, score: 0, longest: 0 }),
})

export type FirebaseUserActions = z.input<typeof zActions>

export const zUser = z.object({
  picture: z.string().optional().default(''),
  actions: zActions.default(zActions.parse({})),
  campaignUpdates: z.array(z.string()).default([]),
  connection: z.string().optional().default(''),
  country: z.string().optional().default('Sverige'),
  createdAt: z.string().optional().default(''),
  email: z.string().optional().default(''),
  emailNotify: z.boolean().default(false),
  familyName: z.string().optional().default(''),
  givenName: z.string().optional().default(''),
  hints: zHints.optional().default(zHints.parse({})),
  id: z.string().optional().default('init'),
  isSocial: z.boolean().default(false),
  name: z.string().optional().default(''),
  userAccess: z.enum(['user', 'admin']).optional().default('user'),
  preferences: zPreferences.optional().default(zPreferences.parse({})),
  partId: z.string().optional().default(''),
  hashId: z.string().optional().default(''),
  birthDate: z.string().optional().default(''),
  ssn: z.string().optional().default(''),
  hash: z.string().optional().default(''),
  onboarding: z
    .object({
      appIntro: z.string().optional().default(''),
      userInput: z.string().optional().default(''),
      userImage: z.string().optional().default(''),
    })
    .optional()
    .default({ appIntro: '', userInput: '', userImage: '' }),
})

export type FirebaseUser = z.input<typeof zUser>

interface Auth0Identity {
  connection: string
  isSocial: boolean
  provider: string
  user_id: string
}

export interface Auth0User {
  created_at: string
  email?: string
  email_verified: boolean
  identities?: Auth0Identity[]
  name: string
  nickname: string
  picture: string
  updated_at: string
  user_id: string
  user_metadata?: {
    country: string
    email_notify: boolean
    user_type?: string
  }
  last_login: string
  last_ip: string
  logins_count: number
  given_name?: string
  family_name?: string
  user_type?: string
  email_notify?: boolean
  connection: string
}

/** Legacy Types  */

export interface ICountry {
  name: string
  code: string
}

export enum IUserType {
  ADMIN = 'rol_jFaxXdOR6w6XmGqr',
}

export interface Users {
  actions: UserActions
  companyUpdates: string[]
  id: string
  preferences: UserPreferences
}

export interface UserPreferences {
  contactForm: boolean
  email: boolean
  facebook: boolean
  instagram: boolean
  tiktok: boolean
  linkedin: boolean
  twitter: boolean
  petition: boolean
  call: boolean
  youtube: boolean
  other: boolean
}

export type HintsType = { [key: string]: string }

export interface UserHints extends UserPreferences {}

interface SnoozedAction {
  id: string
  timeStamp: Date | number
}

export interface UserActions {
  done?: DoneAction[]
  score: number
  hidden?: string[]
  weekScore: {
    data?: number
    score: number
    longest: number
  }
  snoozed: SnoozedAction[] | [] | null
}

export type DoneAction =
  | string
  | {
      id: string
      doneDate: string
      title?: string
      type?: string // actually a card type
    }

export interface UserData extends Users {
  hints: UserHints
  name?: string
  email?: string
  givenName?: string
  familyName?: string
  country?: string
  emailNotify?: boolean
  isSocial?: boolean
}

export interface UserToken {
  name: string
  email: string
  picture?: string | null
  sub: string
  provider?: string
  /* iat: number
  exp: number
  jti: string */
}

export interface IAward {
  icon: number
  text: string
  title: string
}

export interface Auth0Role {
  id: 'rol_jFaxXdOR6w6XmGqr' | 'no-role'
  name: 'Admin' | 'User'
  description: string
}

export const preferencesLabels = {
  contactForm: 'Fylla i kontaktformulär',
  email: 'Skicka mail',
  facebook: 'Facebook',
  instagram: 'Instagram',
  tiktok: 'TikTok',
  linkedin: 'LinkedIn',
  twitter: 'X (Twitter)',
  petition: 'Skriva under ett upprop',
  call: 'Ringa',
  youtube: 'YouTube',
  other: 'Övrigt*',
}
